/// <reference lib="dom" />

import { resolveDelegatedAction, type DelegatedActionCapture } from './delegated-action';

/**
 * Install one capture-phase listener pair for semantic browser actions.
 *
 * `onCapture` also receives the matched control, so a host can route the
 * action by where it sits in the page (for example, a subtree whose actions
 * must not reach behavioural analytics). It runs in the capture phase, before
 * any bubbling handler on the control, so it sees the page the user acted on.
 */
/**
 * Whether activating the control changes nothing because it is read-only. ARIA
 * puts read-only on a radio group rather than on each radio, so a radio or
 * checkbox inside a read-only group counts; any other control nested there
 * (an info toggle inside an option, say) is still a real interaction.
 */
function isReadOnlyControl(control: HTMLElement): boolean {
  if (control.getAttribute('aria-readonly') === 'true') return true;
  const role = control.getAttribute('role');
  return (
    (role === 'radio' || role === 'checkbox') && control.closest('[aria-readonly="true"]') !== null
  );
}

export function installDelegatedActionCapture(
  target: Document,
  onCapture: (capture: DelegatedActionCapture, control: HTMLElement) => void,
): () => void {
  let pendingChangeMethod: 'keyboard' | 'pointer' | undefined;
  const listener = (event: Event) => {
    if (event.type === 'pointerdown') {
      pendingChangeMethod = 'pointer';
      return;
    }
    if (event.type === 'keydown') {
      pendingChangeMethod = 'keyboard';
      return;
    }
    if (event.type !== 'click' && event.type !== 'change') return;
    const origin = event.target instanceof Element ? event.target : null;
    const control = origin?.closest<HTMLElement>('[data-analytics-action]');
    const actionId = control?.dataset.analyticsAction;
    if (!control || !actionId) {
      pendingChangeMethod = undefined;
      return;
    }
    const capture = resolveDelegatedAction({
      actionId,
      elementKey: control.dataset.analyticsElementKey,
      tagName: control.tagName,
      eventType: event.type,
      disabled: control.matches(':disabled'),
      ariaDisabled: control.getAttribute('aria-disabled') === 'true',
      ariaReadOnly: isReadOnlyControl(control),
      manualCapture: control.dataset.analyticsManualCapture === 'true',
      mouseDetail: event instanceof MouseEvent ? event.detail : undefined,
      changeMethod: pendingChangeMethod,
    });
    if (capture) onCapture(capture, control);
    pendingChangeMethod = undefined;
  };
  target.addEventListener('pointerdown', listener, true);
  target.addEventListener('keydown', listener, true);
  target.addEventListener('click', listener, true);
  target.addEventListener('change', listener, true);
  return () => {
    target.removeEventListener('pointerdown', listener, true);
    target.removeEventListener('keydown', listener, true);
    target.removeEventListener('click', listener, true);
    target.removeEventListener('change', listener, true);
  };
}
